"use client"

import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue 
} from '@/components/ui/select'

export interface PaginationProps {
  totalItems: number
  currentPage: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  onItemsPerPageChange: (count: number) => void
}

export function Pagination({
  totalItems,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange
}: PaginationProps) {
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1
  
  // Calculate start and end item numbers for display
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  // Prevent currentPage from exceeding totalPages if items are removed
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      onPageChange(totalPages)
    }
  }, [totalPages, currentPage, onPageChange])

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers: (number | string)[] = []
    const maxPagesToShow = 5 // Show at most 5 page numbers
    
    if (totalPages <= maxPagesToShow) {
      // If we have 5 or fewer pages, show all
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i)
      }
    } else {
      // Always include first page
      pageNumbers.push(1)
      
      // If current page is near the beginning
      if (currentPage <= 3) {
        pageNumbers.push(2, 3, '...', totalPages)
      } 
      // If current page is near the end
      else if (currentPage >= totalPages - 2) {
        pageNumbers.push('...', totalPages - 2, totalPages - 1, totalPages)
      }
      // If current page is in the middle
      else {
        pageNumbers.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages)
      }
    }
    
    return pageNumbers
  }
  
  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 w-full mt-8 mb-4">
      <div className="text-sm text-gray-600">
        {totalItems === 0 
          ? 'No items'
          : `Showing ${startItem}-${endItem} of ${totalItems} items`}
      </div>
      
      <div className="flex items-center">
        <div className="flex items-center mr-6">
          <span className="text-sm mr-2">Items per page:</span>
          <Select
            value={itemsPerPage.toString()}
            onValueChange={(value) => onItemsPerPageChange(parseInt(value))}
          >
            <SelectTrigger className="w-20 h-9">
              <SelectValue placeholder={itemsPerPage} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Previous page</span>
          </Button>
          
          {getPageNumbers().map((page, index) => (
            typeof page === 'number' ? (
              <Button
                key={index}
                variant={currentPage === page ? "default" : "outline"}
                className={`h-9 w-9 ${currentPage === page ? 'bg-primary_green hover:bg-primary_green/90' : ''}`}
                onClick={() => onPageChange(page)}
              >
                {page}
              </Button>
            ) : (
              <Button
                key={index}
                variant="outline"
                className="h-9 w-9 cursor-default"
                disabled
              >
                {page}
              </Button>
            )
          ))}
          
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Next page</span>
          </Button>
        </div>
      </div>
    </div>
  )
}
